// =====================================================
// Video Podcast Admin - Enhanced with Multi-Upload & Voice Narration
// Uses Kling AI for video generation
// =====================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const VideoAdmin = ({ user }) => {
  // Existing state
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [generating, setGenerating] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [clipGenerator, setClipGenerator] = useState({ show: false, type: 'intro', promptType: 'default' });
  
  // NEW: Tab state for switching between modes
  const [activeMode, setActiveMode] = useState('episodes'); // 'episodes' | 'custom'
  
  // NEW: Custom video project state
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [narrationScript, setNarrationScript] = useState(null);
  const [customTab, setCustomTab] = useState('upload'); // 'upload' | 'narration' | 'generate' | 'preview'
  
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'x-user-id': user?.id?.toString() || ''
  });

  useEffect(() => {
    if (user?.id) {
      loadData();
      loadConfig();
      loadProjects();
    }
  }, [user?.id]);

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/video/config`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/video/episodes`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setEpisodes(data.episodes || []);
      }
    } catch (error) {
      console.error('Error loading episodes:', error);
    }
    setLoading(false);
  };

  // =====================================================
  // NEW: Project Management Functions
  // =====================================================

  const loadProjects = async () => {
    try {
      const res = await fetch(`${API_BASE}/video/projects`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadProject = async (projectId) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/video/projects/${projectId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCurrentProject(data.project);
        setScenes(data.scenes || []);
        if (data.project?.narration_script) {
          try {
            setNarrationScript(
              typeof data.project.narration_script === 'string'
                ? JSON.parse(data.project.narration_script)
                : data.project.narration_script
            );
          } catch {
            setNarrationScript(null);
          }
        } else {
          setNarrationScript(null);
        }
      }
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async () => {
    const title = prompt('Enter video title:');
    if (!title) return;
    const topic = prompt('Enter topic (for AI narration):') || title;

    try {
      const res = await fetch(`${API_BASE}/video/projects`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title, topic, style: 'professional' })
      });
      if (res.ok) {
        const data = await res.json();
        await loadProjects();
        loadProject(data.project.project_id);
      }
    } catch (error) {
      alert('Failed to create project: ' + error.message);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Delete this project and all scenes?')) return;
    try {
      await fetch(`${API_BASE}/video/projects/${projectId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      setCurrentProject(null);
      setScenes([]);
      loadProjects();
    } catch (error) {
      alert('Failed to delete: ' + error.message);
    }
  };

  // =====================================================
  // NEW: Multiple File Upload
  // =====================================================

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [currentProject]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    if (!currentProject) {
      alert('Please create or select a project first');
      return;
    }

    const fileArray = Array.from(files);
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/quicktime', 'video/webm'];
    const validFiles = fileArray.filter(file => {
      if (!validTypes.includes(file.type)) return false;
      if (file.size > 50 * 1024 * 1024) return false;
      return true;
    });

    if (validFiles.length === 0) {
      alert('No valid files. Supported: jpeg, png, gif, webp, mp4, mov, webm (max 50MB)');
      return;
    }

    setUploadProgress({ current: 0, total: validFiles.length, status: 'uploading' });

    try {
      const formData = new FormData();
      validFiles.forEach(file => formData.append('images', file));

      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/scenes/upload`, {
        method: 'POST',
        headers: { 'x-user-id': user?.id?.toString() || '' },
        body: formData
      });

      const data = await res.json();
      if (data.success) {
        setUploadProgress({ current: data.uploaded, total: validFiles.length, status: 'complete', errors: data.errors });
        await loadProject(currentProject.project_id);
        setTimeout(() => setUploadProgress(null), 3000);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      setUploadProgress({ status: 'error', error: error.message });
      setTimeout(() => setUploadProgress(null), 5000);
    }
  };

  const deleteScene = async (sceneId) => {
    if (!window.confirm('Delete this scene?')) return;
    try {
      await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/scenes/${sceneId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      await loadProject(currentProject.project_id);
    } catch (error) {
      alert('Failed to delete scene');
    }
  };

  // =====================================================
  // NEW: Narration Generation
  // =====================================================

  const generateNarrationScript = async () => {
    if (scenes.length === 0) {
      alert('Add some scenes first!');
      return;
    }
    setGenerating(prev => ({ ...prev, script: true }));
    try {
      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/narration/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ topic: currentProject.topic || currentProject.title, style: 'professional' })
      });
      const data = await res.json();
      if (data.success) {
        setNarrationScript(data.script);
        alert('✅ Narration script generated!');
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      alert('Failed: ' + error.message);
    } finally {
      setGenerating(prev => ({ ...prev, script: false }));
    }
  };

  const generateNarrationAudio = async () => {
    if (!narrationScript) {
      alert('Generate script first!');
      return;
    }
    setGenerating(prev => ({ ...prev, audio: true }));
    try {
      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/narration/audio`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setCurrentProject(prev => ({ ...prev, narration_audio_url: data.audioUrl }));
        alert('✅ Voice narration generated!');
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      alert('Failed: ' + error.message);
    } finally {
      setGenerating(prev => ({ ...prev, audio: false }));
    }
  };

  // =====================================================
  // NEW: Scene Video Generation
  // =====================================================

  const generateSceneVideo = async (sceneId, prompt) => {
    setGenerating(prev => ({ ...prev, [sceneId]: true }));
    try {
      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/scenes/${sceneId}/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ prompt: prompt || 'Subtle cinematic movement', mode: 'std', duration: '5' })
      });
      const data = await res.json();
      if (data.success) {
        alert(`🎬 Video generation started! Task: ${data.taskId}`);
        pollSceneStatus(sceneId);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      alert('Failed: ' + error.message);
    } finally {
      setGenerating(prev => ({ ...prev, [sceneId]: false }));
    }
  };

  const generateAllVideos = async () => {
    const pending = scenes.filter(s => !s.video_url && s.scene_type === 'image');
    if (!window.confirm(`Generate videos for ${pending.length} scenes?`)) return;

    setGenerating(prev => ({ ...prev, all: true }));
    try {
      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/generate-all`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ mode: 'std', duration: '5' })
      });
      const data = await res.json();
      if (data.success) {
        alert(`🎬 Started ${data.results.filter(r => r.status === 'processing').length} generations!`);
        data.results.forEach(r => { if (r.status === 'processing') pollSceneStatus(r.sceneId); });
      }
    } catch (error) {
      alert('Failed: ' + error.message);
    } finally {
      setGenerating(prev => ({ ...prev, all: false }));
    }
  };

  const pollSceneStatus = async (sceneId, attempts = 0) => {
    if (attempts > 60) return;
    try {
      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/scenes/${sceneId}/status`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.status === 'completed') {
        loadProject(currentProject.project_id);
        return;
      }
      if (data.status === 'error') return;
      setTimeout(() => pollSceneStatus(sceneId, attempts + 1), 10000);
    } catch (error) {
      console.error('Poll error:', error);
    }
  };

  // =====================================================
  // EXISTING: Episode-based functions
  // =====================================================

  const testKlingApi = async () => {
    setTestResult({ testing: true });
    try {
      const response = await fetch(`${API_BASE}/video/test-kling`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setTestResult(data);
    } catch (error) {
      setTestResult({ success: false, error: error.message });
    }
  };

  const generateVideo = async (episodeId, options = {}) => {
    setGenerating(prev => ({ ...prev, [episodeId]: true }));
    try {
      const response = await fetch(`${API_BASE}/video/generate/${episodeId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(options)
      });
      const data = await response.json();
      if (data.success) {
        alert(`✅ Video generation started!\n\nThis will take 5-10 minutes.`);
      } else {
        alert(`❌ Error: ${data.error}`);
      }
      setTimeout(loadData, 3000);
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setGenerating(prev => ({ ...prev, [episodeId]: false }));
  };

  const generateClip = async () => {
    setClipGenerator(prev => ({ ...prev, generating: true }));
    try {
      const response = await fetch(`${API_BASE}/video/generate-clip`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          type: clipGenerator.type,
          promptType: clipGenerator.promptType,
          customPrompt: clipGenerator.customPrompt
        })
      });
      const data = await response.json();
      if (data.success) {
        setClipGenerator(prev => ({ ...prev, result: data, generating: false }));
      } else {
        alert(`Error: ${data.error}`);
        setClipGenerator(prev => ({ ...prev, generating: false }));
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
      setClipGenerator(prev => ({ ...prev, generating: false }));
    }
  };

  const videoStatusColors = {
    null: '#64748b',
    generating: '#f59e0b',
    ready: '#22c55e',
    error: '#ef4444'
  };

  // Show login prompt if no user
  if (!user?.id) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '1rem',
          padding: '2rem',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <h2 style={{ color: '#f87171', margin: '0 0 1rem' }}>🔒 Admin Access Required</h2>
          <p style={{ color: '#94a3b8', margin: 0 }}>Please log in with an admin account.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 100%)', 
      color: '#e2e8f0',
      padding: '1rem'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>🎬 Video Podcast Generator</h1>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem' }}>
            Generate videos from podcasts or create custom video projects with AI narration
          </p>
        </div>

        {/* Configuration Status */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '1rem',
          padding: '1rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(255,255,255,0.08)'
        }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>⚙️ Configuration</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{
              padding: '0.5rem 1rem',
              background: config?.klingConfigured ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              border: `1px solid ${config?.klingConfigured ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              borderRadius: '0.5rem',
              fontSize: '0.875rem'
            }}>
              {config?.klingConfigured ? '✅' : '❌'} Kling AI
            </div>
            <div style={{
              padding: '0.5rem 1rem',
              background: config?.elevenLabsConfigured ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              border: `1px solid ${config?.elevenLabsConfigured ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              borderRadius: '0.5rem',
              fontSize: '0.875rem'
            }}>
              {config?.elevenLabsConfigured ? '✅' : '❌'} Voice (ElevenLabs)
            </div>
            <div style={{
              padding: '0.5rem 1rem',
              background: config?.r2Configured ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              border: `1px solid ${config?.r2Configured ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              borderRadius: '0.5rem',
              fontSize: '0.875rem'
            }}>
              {config?.r2Configured ? '✅' : '❌'} R2 Storage
            </div>
            <button onClick={testKlingApi} disabled={testResult?.testing} style={{
              padding: '0.5rem 1rem',
              background: 'rgba(99, 102, 241, 0.2)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              borderRadius: '0.5rem',
              color: '#a5b4fc',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}>
              {testResult?.testing ? '⏳ Testing...' : '🧪 Test Kling'}
            </button>
          </div>
          {testResult && !testResult.testing && (
            <div style={{
              padding: '0.75rem',
              background: testResult.success ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              borderRadius: '0.5rem',
              fontSize: '0.8rem'
            }}>
              {testResult.success ? (
                <span style={{ color: '#22c55e' }}>✅ {testResult.message}</span>
              ) : (
                <span style={{ color: '#ef4444' }}>❌ {testResult.error}</span>
              )}
            </div>
          )}
        </div>

        {/* Mode Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
          <button
            onClick={() => setActiveMode('episodes')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeMode === 'episodes' ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(30, 41, 59, 0.6)',
              border: activeMode === 'episodes' ? 'none' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: activeMode === 'episodes' ? '600' : '400'
            }}
          >
            📺 From Podcasts
          </button>
          <button
            onClick={() => setActiveMode('custom')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeMode === 'custom' ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(30, 41, 59, 0.6)',
              border: activeMode === 'custom' ? 'none' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: activeMode === 'custom' ? '600' : '400'
            }}
          >
            🎨 Custom Video (Multi-Upload + Voice)
          </button>
        </div>

        {/* =====================================================
            EPISODES MODE (Original functionality)
            ===================================================== */}
        {activeMode === 'episodes' && (
          <>
            {/* Standalone Clip Generator */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.6)',
              borderRadius: '1rem',
              padding: '1rem',
              marginBottom: '1.5rem',
              border: '1px solid rgba(255,255,255,0.08)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: clipGenerator.show ? '1rem' : 0 }}>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>🎨 Generate Standalone Clip</h3>
                <button onClick={() => setClipGenerator(prev => ({ ...prev, show: !prev.show }))} style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(139, 92, 246, 0.2)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '0.5rem',
                  color: '#a78bfa',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}>
                  {clipGenerator.show ? '▼ Hide' : '▶ Show'}
                </button>
              </div>
              
              {clipGenerator.show && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <select value={clipGenerator.type} onChange={(e) => setClipGenerator(prev => ({ ...prev, type: e.target.value, promptType: 'default' }))} style={{ flex: 1, minWidth: '150px', padding: '0.75rem', background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', color: '#e2e8f0' }}>
                      <option value="intro">Intro (5s)</option>
                      <option value="outro">Outro (5s)</option>
                      <option value="transition">Transition (2s)</option>
                    </select>
                    <select value={clipGenerator.promptType} onChange={(e) => setClipGenerator(prev => ({ ...prev, promptType: e.target.value }))} style={{ flex: 1, minWidth: '150px', padding: '0.75rem', background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', color: '#e2e8f0' }}>
                      {clipGenerator.type === 'intro' && (<><option value="default">Default (Logo Reveal)</option><option value="market">Market Theme</option><option value="options">Options Theme</option><option value="crypto">Crypto Theme</option><option value="academy">Academy Theme</option></>)}
                      {clipGenerator.type === 'outro' && (<><option value="default">Default (Subscribe)</option><option value="cta">CTA (Strong)</option></>)}
                      {clipGenerator.type === 'transition' && (<><option value="default">Default (Gradient)</option><option value="data">Data Theme</option><option value="wipe">Diagonal Wipe</option></>)}
                    </select>
                  </div>
                  <button onClick={generateClip} disabled={clipGenerator.generating || !config?.klingConfigured} style={{ padding: '0.75rem 1.5rem', background: clipGenerator.generating ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: clipGenerator.generating ? 'not-allowed' : 'pointer', fontSize: '1rem', fontWeight: '600' }}>
                    {clipGenerator.generating ? '⏳ Generating...' : '🎬 Generate Clip'}
                  </button>
                  {clipGenerator.result && (
                    <div style={{ padding: '1rem', background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '0.5rem' }}>
                      <p style={{ margin: '0 0 0.5rem', color: '#22c55e', fontWeight: '600' }}>✅ Clip Generated!</p>
                      <video src={clipGenerator.result.videoUrl} controls style={{ width: '100%', maxWidth: '400px', borderRadius: '0.5rem' }} />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Episodes List */}
            <div style={{ background: 'rgba(30, 41, 59, 0.6)', borderRadius: '1rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>📺 Episodes with Audio</h3>
                <button onClick={loadData} disabled={loading} style={{ padding: '0.5rem 1rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '0.5rem', color: '#60a5fa', cursor: 'pointer', fontSize: '0.8rem' }}>
                  {loading ? '⏳' : '🔄'} Refresh
                </button>
              </div>
              
              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>Loading...</div>
              ) : episodes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>No episodes found.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {episodes.map((episode) => (
                    <div key={episode.episode_id} style={{ background: 'rgba(51, 65, 85, 0.5)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '0.75rem', padding: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 0.25rem', fontSize: '0.95rem' }}>{episode.title}</h4>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <span style={{ padding: '0.125rem 0.5rem', background: 'rgba(139, 92, 246, 0.2)', borderRadius: '0.25rem', fontSize: '0.7rem', color: '#a78bfa' }}>{episode.category}</span>
                            <span style={{ padding: '0.125rem 0.5rem', background: `${videoStatusColors[episode.video_status] || videoStatusColors.null}20`, borderRadius: '0.25rem', fontSize: '0.7rem', color: videoStatusColors[episode.video_status] || videoStatusColors.null }}>🎬 {episode.video_status || 'No video'}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {(!episode.video_status || episode.video_status === 'error') && (
                            <button onClick={() => generateVideo(episode.episode_id)} disabled={generating[episode.episode_id] || !config?.klingConfigured} style={{ padding: '0.5rem 0.75rem', background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(99, 102, 241, 0.3))', border: '1px solid rgba(139, 92, 246, 0.5)', borderRadius: '0.375rem', color: '#a78bfa', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}>
                              {generating[episode.episode_id] ? '⏳ Starting...' : '🎬 Generate Video'}
                            </button>
                          )}
                          {episode.video_status === 'generating' && (<span style={{ padding: '0.5rem 0.75rem', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '0.375rem', color: '#f59e0b', fontSize: '0.8rem' }}>⏳ Generating...</span>)}
                          {episode.video_status === 'ready' && (<button onClick={() => setSelectedEpisode(selectedEpisode?.episode_id === episode.episode_id ? null : episode)} style={{ padding: '0.5rem 0.75rem', background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '0.375rem', color: '#22c55e', cursor: 'pointer', fontSize: '0.8rem' }}>▶ View Videos</button>)}
                        </div>
                      </div>
                      {episode.video_status === 'ready' && selectedEpisode?.episode_id === episode.episode_id && (
                        <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.5rem' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                            {episode.video_intro_url && (<div><p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: '#94a3b8' }}>🎬 Intro</p><video src={episode.video_intro_url} controls style={{ width: '100%', borderRadius: '0.375rem' }} /></div>)}
                            {episode.video_outro_url && (<div><p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: '#94a3b8' }}>🎬 Outro</p><video src={episode.video_outro_url} controls style={{ width: '100%', borderRadius: '0.375rem' }} /></div>)}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* =====================================================
            CUSTOM MODE (New Multi-Upload + Voice Narration)
            ===================================================== */}
        {activeMode === 'custom' && (
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {/* Projects Sidebar */}
            <div style={{ width: '250px', background: 'rgba(30, 41, 59, 0.6)', borderRadius: '1rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.08)', height: 'fit-content' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 style={{ margin: 0, fontSize: '0.9rem' }}>Projects</h3>
                <button onClick={createProject} style={{ padding: '0.4rem 0.75rem', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: '0.375rem', color: 'white', cursor: 'pointer', fontSize: '0.8rem' }}>+ New</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {projects.map(p => (
                  <div key={p.project_id} onClick={() => loadProject(p.project_id)} style={{ padding: '0.75rem', borderRadius: '0.5rem', background: currentProject?.project_id === p.project_id ? 'rgba(99, 102, 241, 0.2)' : 'rgba(15, 23, 42, 0.5)', border: currentProject?.project_id === p.project_id ? '1px solid rgba(99, 102, 241, 0.5)' : '1px solid transparent', cursor: 'pointer' }}>
                    <div style={{ fontWeight: '600', fontSize: '0.85rem' }}>{p.title}</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>{p.status} • {new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
                {projects.length === 0 && <div style={{ textAlign: 'center', padding: '1rem', color: '#64748b', fontSize: '0.8rem' }}>No projects yet</div>}
              </div>
            </div>

            {/* Main Content */}
            <div style={{ flex: 1, minWidth: '300px', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '1rem', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.08)' }}>
              {!currentProject ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                  <h2>👋 Create a Project</h2>
                  <p>Click "+ New" to start a custom video project with multiple images and AI voice narration.</p>
                  <button onClick={createProject} style={{ marginTop: '1rem', padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer', fontWeight: '600' }}>🎬 Create Project</button>
                </div>
              ) : (
                <>
                  {/* Project Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{currentProject.title}</h2>
                      <p style={{ color: '#94a3b8', marginTop: '0.25rem', fontSize: '0.85rem' }}>Topic: {currentProject.topic || 'Not set'}</p>
                    </div>
                    <button onClick={() => deleteProject(currentProject.project_id)} style={{ padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '0.5rem', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem' }}>🗑️ Delete</button>
                  </div>

                  {/* Custom Tabs */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                    {[
                      { id: 'upload', label: '📤 Upload Scenes' },
                      { id: 'narration', label: '🎤 Voice Narration' },
                      { id: 'generate', label: '🎬 Generate Videos' },
                      { id: 'preview', label: '👁️ Preview' }
                    ].map(tab => (
                      <button key={tab.id} onClick={() => setCustomTab(tab.id)} style={{ padding: '0.6rem 1rem', background: customTab === tab.id ? 'rgba(99, 102, 241, 0.2)' : 'rgba(15, 23, 42, 0.5)', border: customTab === tab.id ? '1px solid rgba(99, 102, 241, 0.5)' : '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', color: customTab === tab.id ? '#a5b4fc' : '#94a3b8', cursor: 'pointer', fontSize: '0.85rem' }}>{tab.label}</button>
                    ))}
                  </div>

                  {/* UPLOAD TAB */}
                  {customTab === 'upload' && (
                    <div>
                      <div
                        style={{ border: `2px dashed ${dragActive ? '#6366f1' : 'rgba(99, 102, 241, 0.5)'}`, borderRadius: '1rem', padding: '2rem', textAlign: 'center', cursor: 'pointer', background: dragActive ? 'rgba(99, 102, 241, 0.1)' : 'rgba(15, 23, 42, 0.3)', marginBottom: '1.5rem', transition: 'all 0.2s' }}
                        onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm" onChange={handleFileInput} style={{ display: 'none' }} />
                        <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '0.5rem' }}>📁</span>
                        <h3 style={{ margin: '0 0 0.5rem' }}>Drop images here or click to browse</h3>
                        <p style={{ color: '#64748b', margin: 0, fontSize: '0.85rem' }}>Supports: JPEG, PNG, GIF, WebP, MP4, MOV (max 50MB each)</p>
                        <p style={{ color: '#6366f1', margin: '0.5rem 0 0', fontSize: '0.8rem' }}>💡 Ctrl/Cmd + Click to select multiple files</p>
                      </div>

                      {uploadProgress && (
                        <div style={{ padding: '1rem', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '0.5rem', marginBottom: '1rem', textAlign: 'center' }}>
                          {uploadProgress.status === 'uploading' && <p>⏳ Uploading... {uploadProgress.current}/{uploadProgress.total}</p>}
                          {uploadProgress.status === 'complete' && <p style={{ color: '#22c55e' }}>✅ Uploaded {uploadProgress.current} files!</p>}
                          {uploadProgress.status === 'error' && <p style={{ color: '#ef4444' }}>❌ {uploadProgress.error}</p>}
                        </div>
                      )}

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1rem' }}>
                        {scenes.map((scene, index) => (
                          <div key={scene.id} style={{ position: 'relative', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', background: 'rgba(0,0,0,0.7)', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.7rem', zIndex: 1 }}>Scene {index + 1}</div>
                            {scene.source_url && <img src={scene.source_url} alt={`Scene ${index + 1}`} style={{ width: '100%', height: '120px', objectFit: 'cover' }} />}
                            <div style={{ padding: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{scene.scene_type === 'video' ? '🎬' : '🖼️'}</span>
                              <span style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', borderRadius: '0.25rem', background: scene.status === 'completed' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(100, 116, 139, 0.3)', color: scene.status === 'completed' ? '#22c55e' : '#94a3b8' }}>{scene.status}</span>
                            </div>
                            <button onClick={() => deleteScene(scene.id)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'rgba(239, 68, 68, 0.8)', border: 'none', borderRadius: '50%', width: '20px', height: '20px', color: 'white', cursor: 'pointer', fontSize: '0.8rem', lineHeight: '1' }}>×</button>
                          </div>
                        ))}
                      </div>
                      {scenes.length === 0 && <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No scenes yet. Upload images above!</div>}
                    </div>
                  )}

                  {/* NARRATION TAB */}
                  {customTab === 'narration' && (
                    <div>
                      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                        <button onClick={generateNarrationScript} disabled={generating.script || scenes.length === 0} style={{ padding: '0.75rem 1.5rem', background: generating.script ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: generating.script ? 'not-allowed' : 'pointer', fontWeight: '600' }}>
                          {generating.script ? '⏳ Generating...' : '📝 Generate Script'}
                        </button>
                        {narrationScript && (
                          <button onClick={generateNarrationAudio} disabled={generating.audio} style={{ padding: '0.75rem 1.5rem', background: generating.audio ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: generating.audio ? 'not-allowed' : 'pointer', fontWeight: '600' }}>
                            {generating.audio ? '⏳ Generating...' : '🎤 Generate Voice Audio'}
                          </button>
                        )}
                      </div>

                      {narrationScript && (
                        <div style={{ background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1rem' }}>
                          <h3 style={{ margin: '0 0 1rem' }}>📜 Generated Script</h3>
                          <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <h4 style={{ margin: '0 0 0.5rem', color: '#a5b4fc' }}>Title</h4>
                            <p style={{ margin: 0 }}>{narrationScript.title}</p>
                          </div>
                          <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <h4 style={{ margin: '0 0 0.5rem', color: '#a5b4fc' }}>Introduction</h4>
                            <p style={{ margin: 0 }}>{narrationScript.introduction}</p>
                          </div>
                          {narrationScript.scenes?.map((s, i) => (
                            <div key={i} style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                              <h4 style={{ margin: '0 0 0.25rem', color: '#94a3b8', fontSize: '0.85rem' }}>Scene {s.sceneNumber}</h4>
                              <p style={{ margin: 0, fontSize: '0.9rem' }}>{s.narration}</p>
                              <small style={{ color: '#64748b' }}>~{s.duration_estimate_seconds}s</small>
                            </div>
                          ))}
                          <div>
                            <h4 style={{ margin: '0 0 0.5rem', color: '#a5b4fc' }}>Conclusion</h4>
                            <p style={{ margin: 0 }}>{narrationScript.conclusion}</p>
                          </div>
                        </div>
                      )}

                      {currentProject.narration_audio_url && (
                        <div style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '0.75rem', padding: '1rem' }}>
                          <h3 style={{ margin: '0 0 0.75rem' }}>🎧 Voice Narration</h3>
                          <audio controls src={currentProject.narration_audio_url} style={{ width: '100%' }} />
                          <a href={currentProject.narration_audio_url} download style={{ display: 'inline-block', marginTop: '0.75rem', color: '#60a5fa', textDecoration: 'none', fontSize: '0.85rem' }}>📥 Download Audio</a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* GENERATE TAB */}
                  {customTab === 'generate' && (
                    <div>
                      <div style={{ marginBottom: '1.5rem' }}>
                        <button onClick={generateAllVideos} disabled={generating.all || scenes.filter(s => !s.video_url && s.scene_type === 'image').length === 0} style={{ padding: '0.75rem 1.5rem', background: generating.all ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: generating.all ? 'not-allowed' : 'pointer', fontWeight: '600' }}>
                          {generating.all ? '⏳ Processing...' : `🎬 Generate All (${scenes.filter(s => !s.video_url && s.scene_type === 'image').length} scenes)`}
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                        {scenes.map((scene, index) => (
                          <div key={scene.id} style={{ position: 'relative', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', padding: '0.5rem' }}>
                            <div style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}>Scene {index + 1}</div>
                            {scene.source_url && <img src={scene.source_url} alt={`Scene ${index + 1}`} style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '0.5rem' }} />}
                            <div style={{ padding: '0.5rem 0', textAlign: 'center', fontSize: '0.8rem' }}>
                              {scene.video_url ? <span style={{ color: '#22c55e' }}>✅ Video Ready</span> : scene.status === 'processing' ? <span style={{ color: '#f59e0b' }}>⏳ Processing...</span> : <span style={{ color: '#94a3b8' }}>⏸️ Pending</span>}
                            </div>
                            {scene.video_url && <video src={scene.video_url} controls style={{ width: '100%', borderRadius: '0.5rem', marginTop: '0.5rem' }} />}
                            {!scene.video_url && scene.scene_type === 'image' && (
                              <button onClick={() => { const p = window.prompt('Motion prompt:', 'Subtle cinematic movement'); if (p) generateSceneVideo(scene.id, p); }} disabled={generating[scene.id] || scene.status === 'processing'} style={{ width: '100%', padding: '0.5rem', background: 'rgba(99, 102, 241, 0.2)', border: '1px solid rgba(99, 102, 241, 0.3)', borderRadius: '0.5rem', color: '#a5b4fc', cursor: 'pointer', marginTop: '0.5rem', fontSize: '0.8rem' }}>🎬 Generate</button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* PREVIEW TAB */}
                  {customTab === 'preview' && (
                    <div>
                      <h3 style={{ margin: '0 0 1rem' }}>🎥 Video Preview</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {scenes.map((scene, index) => (
                          <div key={scene.id} style={{ background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', padding: '1rem' }}>
                            <h4 style={{ margin: '0 0 0.75rem' }}>Scene {index + 1}</h4>
                            {scene.video_url ? (
                              <video src={scene.video_url} controls style={{ width: '100%', maxHeight: '300px', borderRadius: '0.5rem' }} />
                            ) : scene.source_url ? (
                              <img src={scene.source_url} alt={`Scene ${index + 1}`} style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '0.5rem' }} />
                            ) : null}
                            {scene.narration_text && <p style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '0.5rem', fontSize: '0.9rem' }}>🎤 {scene.narration_text}</p>}
                          </div>
                        ))}
                      </div>
                      {currentProject.narration_audio_url && (
                        <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '0.75rem' }}>
                          <h4 style={{ margin: '0 0 0.75rem' }}>🎧 Full Narration</h4>
                          <audio controls src={currentProject.narration_audio_url} style={{ width: '100%' }} />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '0.75rem', fontSize: '0.8rem', color: '#93c5fd' }}>
          <h4 style={{ margin: '0 0 0.5rem' }}>📋 How It Works</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div>
              <strong>📺 From Podcasts:</strong>
              <ol style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                <li>Generate audio podcast first</li>
                <li>Click "Generate Video" for intro/outro</li>
                <li>Videos ready in 5-10 minutes</li>
              </ol>
            </div>
            <div>
              <strong>🎨 Custom Video:</strong>
              <ol style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                <li>Create project, upload multiple images</li>
                <li>Generate AI narration script + voice</li>
                <li>Generate videos for each scene</li>
                <li>Preview with audio narration</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoAdmin;